import React from 'react';
import { Button, Card } from 'react-bootstrap';
import {
  BloodworkAnalysis,
  FurtherConsiderations,
  Section,
} from '../../models';
import { BloodworkEntryStore } from '../../stores/bloodwork-entry-store';
import { parseSupplementItem } from '../../utils/supplementItem';

// Per-system PDF labels in canonical report order. Drives both the order of
// section blocks within each brand and the labels we show.
const SECTION_HEADINGS: { section: Section; label: string }[] = [
  { section: Section.RBC,                           label: 'Blood Oxygen & Red Blood Cell Function' },
  { section: Section.IRON,                          label: 'Iron Status & Utilization' },
  { section: Section.WBC,                           label: 'Immune Activity' },
  { section: Section.PLATELETS,                     label: 'Blood Clotting Function' },
  { section: Section.GLUCOSE,                       label: 'Glucose Regulation & Metabolic Function' },
  { section: Section.KIDNEY,                        label: 'Kidney Function' },
  { section: Section.PROTEINS,                      label: 'Protein Status' },
  { section: Section.NUTRIENTS,                     label: 'Vitamin & Mineral Status' },
  { section: Section.ELECTROLYTES,                  label: 'Electrolyte Balance' },
  { section: Section.LIVER_BILIARY,                 label: 'Liver Function' },
  { section: Section.LIPIDS,                        label: 'Lipid Balance' },
  { section: Section.INFLAMMATION_OXIDATIVE_STRESS, label: 'Inflammation & Oxidative Stress' },
  { section: Section.THYROID,                       label: 'Thyroid Function' },
  { section: Section.STRESS_ADRENAL,                label: 'Stress & Adrenal Balance' },
  { section: Section.SEX_HORMONE,                   label: 'Hormone Balance' },
  { section: Section.PROSTATE,                      label: 'Prostate Function' },
];

const BRAND_LABELS: { [brand: string]: string } = {
  systemicFormulas: 'Systemic Formulas',
  bwhLabs: 'BWH Labs',
};
const brandLabel = (brand: string): string => BRAND_LABELS[brand] ?? brand;

// A Supplements row's title reads "<Brand> — Supporting Supplement(s) for
// <Pattern>" (plural for multi-product brands like Systemic Formulas, singular
// for single-product brands like BWH Labs). The summary-panel section heading
// pairs the system label (e.g. "Immune Activity") with the specific pattern
// that drove those supplements — "Immune Activity — Suppressed Immune
// Activity". Returns null when a title doesn't follow the convention, so the
// heading falls back to the bare label.
const PATTERN_TITLE_RE = / Supporting Supplements? for (.+)$/;
function patternNameFromTitle(title?: string): string | null {
  if (!title) return null;
  const match = PATTERN_TITLE_RE.exec(title);
  return match ? match[1].trim() : null;
}

type Props = {
  bloodworkAnalysis: BloodworkAnalysis;
  furtherConsiderations: FurtherConsiderations;
  enabledSupplementBrands?: string[];
  forPdf?: boolean;
  includeInPdf?: boolean;
  onIncludeInPdfToggle?: () => void;
  // Required for Edit-as-override. When absent, the Edit button is hidden.
  bloodworkEntryStore?: BloodworkEntryStore;
};

type State = {
  editing: boolean;
  draftText: string;
};

type Supplement = { name: string; description: string; link?: string };
type SectionGroup = {
  section: Section;
  label: string;
  supplements: Supplement[];
};
type BrandGroup = {
  brand: string;
  sections: SectionGroup[];
};

// Thin alias over the shared parser so the rest of this file keeps reading
// parseItem(...). The separator/URL logic lives in src/utils/supplementItem.ts
// so this panel and FurtherConsiderationsSection can never drift apart.
function parseItem(item: string): Supplement | null {
  return parseSupplementItem(item);
}

function renderSupplementName(s: Supplement): JSX.Element {
  if (!s.link) return <strong>{s.name}</strong>;
  return (
    <a
      href={s.link}
      target="_blank"
      rel="noopener noreferrer"
      style={{ fontWeight: 'bold' }}
    >
      {s.name}
    </a>
  );
}

function collectByBrandAndSection(
  bloodworkAnalysis: BloodworkAnalysis,
  furtherConsiderations: FurtherConsiderations,
  brands: string[] | undefined,
): BrandGroup[] {
  type Bucket = {
    section: Section;
    label: string;
    // Distinct pattern names (insertion-ordered) whose supplements landed in
    // this section — appended to the heading after the system label.
    patterns: Set<string>;
    supplements: Map<string, Supplement>;
  };
  const byBrand = new Map<string, Map<number, Bucket>>();

  SECTION_HEADINGS.forEach(({ section, label }, sectionIdx) => {
    const categories =
      bloodworkAnalysis.possibleRootCauseCategoriesFor?.(section) ?? [];
    categories.forEach(category => {
      const matches = furtherConsiderations.findConsiderations(
        category.rootCauseCategory,
        section,
        'Supplements',
        brands,
      );
      matches.forEach(consideration => {
        const brand = consideration.brand || 'unknown';
        if (!byBrand.has(brand)) byBrand.set(brand, new Map());
        const brandMap = byBrand.get(brand)!;
        if (!brandMap.has(sectionIdx)) {
          brandMap.set(sectionIdx, {
            section,
            label,
            patterns: new Set(),
            supplements: new Map(),
          });
        }
        const bucket = brandMap.get(sectionIdx)!;
        const patternName = patternNameFromTitle(consideration.title);
        if (patternName) bucket.patterns.add(patternName);
        (consideration.items || []).forEach(item => {
          // resolveSupplement handles both a supplement-library tag (resolved
          // via findByTag) and a legacy inline "Name — Description — url" string.
          const parsed = furtherConsiderations.resolveSupplement(item);
          if (!parsed) return;
          if (!bucket.supplements.has(parsed.name)) {
            bucket.supplements.set(parsed.name, parsed);
          }
        });
      });
    });
  });

  return Array.from(byBrand.entries())
    .map(([brand, sectionMap]) => ({
      brand,
      sections: Array.from(sectionMap.entries())
        .sort(([a], [b]) => a - b)
        .map(([, bucket]) => ({
          section: bucket.section,
          label: bucket.patterns.size
            ? `${bucket.label} — ${Array.from(bucket.patterns).join(', ')}`
            : bucket.label,
          supplements: Array.from(bucket.supplements.values()),
        }))
        .filter(s => s.supplements.length > 0),
    }))
    .filter(g => g.sections.length > 0)
    .sort((a, b) => brandLabel(a.brand).localeCompare(brandLabel(b.brand)));
}

// Lightweight markdown-ish convention used by the override textarea. The
// rendered view parses these markers back into the same brand / section /
// bullet styling as the auto-generated content.
//   "## Brand"            → brand heading (h4)
//   "### Section"          → section heading (h6)
//   "- Name — Description" → bullet with bold name
//   anything else          → plain paragraph
function groupsToText(groups: BrandGroup[]): string {
  return groups
    .map(({ brand, sections }) => {
      const brandLine = `## ${brandLabel(brand)}`;
      const sectionBlocks = sections
        .map(({ label, supplements }) => {
          const bullets = supplements
            .map(s => {
              const base = `- ${s.name} — ${s.description}`;
              return s.link ? `${base} — ${s.link}` : base;
            })
            .join('\n');
          return `### ${label}\n${bullets}`;
        })
        .join('\n\n');
      return `${brandLine}\n\n${sectionBlocks}`;
    })
    .join('\n\n');
}

function renderOverride(override: string): JSX.Element {
  const lines = override.split('\n');
  const nodes: JSX.Element[] = [];
  let bullets: string[] = [];

  const flushBullets = () => {
    if (bullets.length === 0) return;
    nodes.push(
      <ul key={`ul-${nodes.length}`} style={{ marginBottom: 8 }}>
        {bullets.map((b, i) => {
          const supp = parseItem(b);
          return supp ? (
            <li key={i}>
              {renderSupplementName(supp)} — {supp.description}
            </li>
          ) : (
            <li key={i}>{b}</li>
          );
        })}
      </ul>,
    );
    bullets = [];
  };

  lines.forEach((line, idx) => {
    const trimmed = line.trim();
    if (line.startsWith('- ')) {
      bullets.push(line.slice(2));
    } else if (trimmed.startsWith('### ')) {
      flushBullets();
      nodes.push(
        <h6
          key={`h6-${idx}`}
          style={{
            marginTop: nodes.length > 0 ? 14 : 0,
            marginBottom: 6,
            marginLeft: 8,
            fontSize: '1rem',
            fontWeight: 'bold',
          }}
        >
          {trimmed.slice(4)}
        </h6>,
      );
    } else if (trimmed.startsWith('## ')) {
      flushBullets();
      nodes.push(
        <h4
          key={`h4-${idx}`}
          style={{
            marginTop: nodes.length > 0 ? 18 : 0,
            marginBottom: 12,
            fontSize: '1.5rem',
          }}
        >
          <strong>{trimmed.slice(3)}</strong>
        </h4>,
      );
    } else if (trimmed === '') {
      flushBullets();
    } else {
      flushBullets();
      nodes.push(<div key={`p-${idx}`}>{line}</div>);
    }
  });
  flushBullets();

  return <>{nodes}</>;
}

function readOverride(bloodworkAnalysis: BloodworkAnalysis): string {
  const note = bloodworkAnalysis?.bloodwork?.bloodworkNotes?.findNote(
    Section.SUPPLEMENTS,
  );
  return (note?.text ?? '').trimStart();
}

export class SupplementsSummaryPanel extends React.Component<Props, State> {
  state: State = {
    editing: false,
    draftText: '',
  };

  private editTextareaEl: HTMLTextAreaElement | null = null;

  componentDidUpdate(_prevProps: Props, prevState: State) {
    if (this.state.editing && !prevState.editing) {
      this.fitTextareaToContent();
    }
  }

  private setEditTextareaRef = (el: HTMLTextAreaElement | null) => {
    this.editTextareaEl = el;
  };

  private fitTextareaToContent = () => {
    const el = this.editTextareaEl;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
    el.style.overflow = 'hidden';
  };

  private computeGroups(): BrandGroup[] {
    const { bloodworkAnalysis, furtherConsiderations, enabledSupplementBrands } =
      this.props;
    return collectByBrandAndSection(
      bloodworkAnalysis,
      furtherConsiderations,
      enabledSupplementBrands,
    );
  }

  // Rough height (in px, html2pdf canvas units) of the rendered PDF panel.
  // Used by the parent container to decide whether the Disclaimer card fits
  // below Supplements on the same page or needs to break to its own page.
  // Errs on the high side — overestimating just pushes the disclaimer to a
  // fresh page when it might have fit; underestimating risks overflow.
  static estimatedPdfHeight(
    bloodworkAnalysis: BloodworkAnalysis,
    furtherConsiderations: FurtherConsiderations,
    enabledSupplementBrands?: string[],
  ): number {
    if (!bloodworkAnalysis?.bloodwork || !furtherConsiderations) return 0;
    const override = readOverride(bloodworkAnalysis);
    const CARD_OVERHEAD = 100;
    if (override) {
      let h = CARD_OVERHEAD;
      override.split('\n').forEach(line => {
        const t = line.trim();
        if (t.startsWith('## ')) h += 60;
        else if (t.startsWith('### ')) h += 40;
        else if (line.startsWith('- ')) h += 26;
        else if (t === '') h += 8;
        else h += 24;
      });
      return h;
    }
    const groups = collectByBrandAndSection(
      bloodworkAnalysis,
      furtherConsiderations,
      enabledSupplementBrands,
    );
    if (groups.length === 0) return 0;
    let h = CARD_OVERHEAD;
    groups.forEach(({ sections }) => {
      h += 65;
      sections.forEach(({ supplements }) => {
        h += 45;
        h += supplements.length * 30;
      });
    });
    return h;
  }

  private handleEditClick = () => {
    const override = readOverride(this.props.bloodworkAnalysis);
    const draft = override || groupsToText(this.computeGroups());
    this.setState({ editing: true, draftText: draft });
  };

  private handleCancel = () => {
    this.setState({ editing: false, draftText: '' });
  };

  private writeNote = (text: string) => {
    const notes = this.props.bloodworkAnalysis.bloodwork.bloodworkNotes;
    let note = notes.findNote(Section.SUPPLEMENTS);
    if (!note) note = notes.addNote(Section.SUPPLEMENTS, text);
    note.text = text;
  };

  // BloodworkNotes (the Practitioner Notes / Insights panel) keeps the
  // scroll position because it never re-renders its DOM around the save —
  // the textarea stays mounted throughout. Our panel does flip from edit
  // mode (tall textarea) to view mode (formatted content), which can shift
  // layout, and the global spinner modal opening/closing on top of that
  // shift can leave the page scrolled to the top. To mimic the
  // "stay where you are" feel we explicitly capture scrollY before the
  // save kicks off and restore it on both sides of the save:
  //   - immediately after the setState that flips to view mode (in case
  //     layout shifted before the spinner appeared), and
  //   - again when the save promise resolves (after the spinner closes
  //     and react-bootstrap's modal does its own scroll restore).
  private restoreScrollAfterPaint = (y: number) => {
    requestAnimationFrame(() => window.scrollTo(0, y));
  };

  private handleSave = (e?: React.MouseEvent) => {
    if (e) e.preventDefault();
    const { bloodworkAnalysis, bloodworkEntryStore } = this.props;
    if (!bloodworkEntryStore) return;
    const scrollY = window.scrollY;
    this.writeNote(this.state.draftText);
    bloodworkEntryStore
      .saveBloodwork(bloodworkAnalysis.bloodwork)
      .then(() => this.restoreScrollAfterPaint(scrollY))
      .catch(err => {
        console.error('Failed to save Supplements override', err);
        alert('Failed to save your changes. Please try again.');
      });
    this.setState({ editing: false, draftText: '' }, () =>
      this.restoreScrollAfterPaint(scrollY),
    );
  };

  private handleReset = (e?: React.MouseEvent) => {
    if (e) e.preventDefault();
    const { bloodworkAnalysis, bloodworkEntryStore } = this.props;
    if (!bloodworkEntryStore) return;
    if (
      !window.confirm(
        'Clear your edits and restore the auto-generated Supplements list?',
      )
    ) {
      return;
    }
    const scrollY = window.scrollY;
    this.writeNote('');
    bloodworkEntryStore
      .saveBloodwork(bloodworkAnalysis.bloodwork)
      .then(() => this.restoreScrollAfterPaint(scrollY))
      .catch(err => {
        console.error('Failed to reset Supplements override', err);
        alert('Failed to reset. Please try again.');
      });
    this.setState({ editing: false, draftText: '' }, () =>
      this.restoreScrollAfterPaint(scrollY),
    );
  };

  render() {
    const {
      bloodworkAnalysis,
      furtherConsiderations,
      forPdf,
      includeInPdf,
      onIncludeInPdfToggle,
      bloodworkEntryStore,
    } = this.props;
    const { editing, draftText } = this.state;

    if (!bloodworkAnalysis?.bloodwork || !furtherConsiderations) {
      return null;
    }

    const groups = this.computeGroups();
    const override = readOverride(bloodworkAnalysis);

    if (groups.length === 0 && !override) {
      return null;
    }

    const generatedBody = (
      <>
        {groups.map(({ brand, sections }) => (
          <div key={brand} style={{ marginBottom: 20 }}>
            <h4 style={{ marginBottom: 12, fontSize: '1.5rem' }}>
              <strong>{brandLabel(brand)}</strong>
            </h4>
            {sections.map(({ section, label, supplements }) => (
              <div key={section} style={{ marginBottom: 14, marginLeft: 8 }}>
                <h6
                  style={{
                    marginBottom: 6,
                    fontSize: '1rem',
                    fontWeight: 'bold',
                  }}
                >
                  {label}
                </h6>
                <ul style={{ marginBottom: 0 }}>
                  {supplements.map(s => (
                    <li key={s.name}>
                      {renderSupplementName(s)}
                      {s.description ? ` — ${s.description}` : ''}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        ))}
      </>
    );

    const body = override ? renderOverride(override) : generatedBody;

    if (forPdf) {
      return (
        <Card className="disclaimer-panel mb-5">
          <Card.Header className="py-1">Supplements</Card.Header>
          <Card.Body
            className="disclaimer-body"
            style={{ paddingTop: 4, paddingBottom: 12 }}
          >
            {body}
          </Card.Body>
        </Card>
      );
    }

    // The viewed body is wrapped in a bordered box so the Edit button below
    // it visually anchors to the content (mirrors the textarea+Save layout
    // used by the Insights notes pattern above this panel).
    const viewBody = (
      <>
        <div
          style={{
            border: '1px solid #ced4da',
            borderRadius: 4,
            padding: 12,
            backgroundColor: '#fff',
          }}
        >
          {body}
        </div>
        {bloodworkEntryStore && (
          <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
            <Button
              type="button"
              variant="primary"
              title="Edit Supplements"
              onClick={this.handleEditClick}
            >
              Edit
            </Button>
            {override && (
              <Button
                type="button"
                variant="outline-danger"
                title="Discard your edits and restore the auto-generated list with clickable supplement names"
                onClick={this.handleReset}
              >
                Reset to default
              </Button>
            )}
          </div>
        )}
      </>
    );

    const editBody = (
      <>
        <div
          style={{
            fontSize: '12px',
            color: '#6c757d',
            marginBottom: 6,
          }}
        >
          Formatting:&nbsp;
          <code>## Brand</code>&nbsp;for brand headings,&nbsp;
          <code>### Section</code>&nbsp;for section headings.
        </div>
        <textarea
          ref={this.setEditTextareaRef}
          value={draftText}
          onChange={e => this.setState({ draftText: e.target.value }, this.fitTextareaToContent)}
          rows={24}
          style={{
            width: '100%',
            fontFamily: 'inherit',
            fontSize: '14px',
            padding: 8,
            borderRadius: 4,
            border: '1px solid #ced4da',
          }}
        />
        <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
          <Button variant="primary" onClick={this.handleSave}>
            Save
          </Button>
          <Button variant="secondary" onClick={this.handleCancel}>
            Cancel
          </Button>
          {override && (
            <Button
              variant="outline-danger"
              onClick={this.handleReset}
              style={{ marginLeft: 'auto' }}
              title="Discard your edits and restore the auto-generated list"
            >
              Reset to default
            </Button>
          )}
        </div>
      </>
    );

    return (
      <Card>
        <Card.Header
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <span>Supplements</span>
          {!editing && onIncludeInPdfToggle && (
            <label
              htmlFor="include-in-pdf-supplements"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                margin: 0,
                fontWeight: 'normal',
                cursor: 'pointer',
                color: '#fff',
              }}
              title="Include this section in the Analysis PDF"
            >
              <span style={{ marginRight: '6px' }}>Include in PDF</span>
              <input
                id="include-in-pdf-supplements"
                type="checkbox"
                className="lsm-styled-checkbox"
                checked={!!includeInPdf}
                onChange={onIncludeInPdfToggle}
              />
            </label>
          )}
        </Card.Header>
        <Card.Body>{editing ? editBody : viewBody}</Card.Body>
      </Card>
    );
  }
}
